import moment from 'moment';
import { put, takeLatest } from 'redux-saga/effects';
import { AppTableInitialState, createAppTableSaga, createAppTableStore } from 'components/AppTable/store';
import apiRoutes from 'config/apiRoute';

const DATE_FORMAT = 'YYYY-MM-DD';
const defaultStart = moment().subtract(29, 'days').format(DATE_FORMAT);
const defaultEnd = moment().format(DATE_FORMAT);

const toReportParams = (_params: Record<string, any>, state: AppTableInitialState) => {
  const filters = (state.params.filters ?? {}) as Record<string, { value?: string }>;
  const next: Record<string, string> = {};

  if (filters.date_start?.value) next.start_date = String(filters.date_start.value);
  if (filters.date_end?.value) next.end_date = String(filters.date_end.value);

  return next;
};

const baseInitialState = {
  serverControl: false,
  params: {
    filters: {
      date_start: { value: defaultStart },
      date_end: { value: defaultEnd },
    },
  },
  apiParamsHandler: toReportParams,
};

export interface ClockTotals {
  avgClockIn: string | null;
  avgClockOut: string | null;
}

const flattenClockDays = (data: any[]) =>
  (Array.isArray(data) ? data : []).flatMap((entry) =>
    (entry?.days ?? []).map((day: any) => ({
      uuid: `${entry?.user?.uuid ?? 'user'}-${day?.date}`,
      date: day?.date,
      clock_in: day?.clock_in ?? day?.clock_int,
      clock_out: day?.clock_out,
      logged: day?.logged,
      span: day?.span,
    })),
  );

export const profileClockReportSlice = createAppTableStore({
  name: 'profileClockReport' as const,
  url: apiRoutes.CLOCK_REPORT,
  transformData: flattenClockDays,
  initialState: {
    ...baseInitialState,
    clockTotals: null as ClockTotals | null,
  },
  reducers: {
    setClockTotals: (state, { payload }: { payload: ClockTotals | null }) => {
      state.clockTotals = payload;
    },
  },
});

function* handleClockTotals({ payload }: { payload: any }) {
  const totals = payload?.data?.[0]?.totals;

  yield put(
    profileClockReportSlice.actions.setClockTotals(
      totals ? { avgClockIn: totals.avg_clock_in ?? null, avgClockOut: totals.avg_clock_out ?? null } : null,
    ),
  );
}

export const profileClockReportSaga = createAppTableSaga(profileClockReportSlice, function* customGenerator() {
  yield takeLatest(profileClockReportSlice.actions.handleApiResponse, handleClockTotals);
});

const createReportStore = <Name extends string>(name: Name, url: string) =>
  createAppTableStore({ name, url, initialState: baseInitialState });

export const profileTaskOverdueSlice = createReportStore('profileTaskOverdue', apiRoutes.REPORT_TASK_OVERDUE);
export const profileRequestOverdueSlice = createReportStore('profileRequestOverdue', apiRoutes.REPORT_OVERDUE);

export const profileTaskOverdueSaga = createAppTableSaga(profileTaskOverdueSlice);
export const profileRequestOverdueSaga = createAppTableSaga(profileRequestOverdueSlice);

export const profileClockReportReducer = profileClockReportSlice.reducer;
export const profileTaskOverdueReducer = profileTaskOverdueSlice.reducer;
export const profileRequestOverdueReducer = profileRequestOverdueSlice.reducer;
